/**
 * Milkath Admin panel.
 *
 * Only reachable when the logged-in user's email is admin@milkath.com
 * (enforced both on the backend via /api/admin/* and on the frontend by
 * the <AdminOnly /> guard).
 *
 * Shows platform-wide totals, recent activity, system health, storage
 * usage and a per-org table with a "Reset Organization" action.
 */
import React, { useEffect, useState } from "react";
import {
  ShieldAlert, Building2, Users, Upload, ShoppingBag, Package, HeartHandshake,
  Activity, Database, HardDrive, RefreshCw, Trash2, Loader2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { formatNumber as num } from "../lib/format";

function bytesToLabel(b) {
  if (!b) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = b, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
}

function TotalCard({ label, value, icon: Icon, testid }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-5" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-zinc-950 border border-zinc-800 flex items-center justify-center">
          <Icon className="h-4 w-4 text-orange-400" strokeWidth={1.7} />
        </div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">{label}</p>
      </div>
      <p
        className="mt-4 text-3xl font-semibold tracking-tight text-zinc-50 tabular-nums"
        style={{ fontFamily: "var(--font-heading)" }}
      >
        {num(value)}
      </p>
    </div>
  );
}

function ResetOrgDialog({ org, onCancel, onConfirmed }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/organizations/${org.organization_id}/reset`);
      toast.success(`Reset ${org.name}`);
      onConfirmed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="admin-reset-dialog">
      <div className="absolute inset-0 bg-black/70" onClick={busy ? undefined : onCancel} />
      <div className="relative w-full max-w-lg bg-zinc-900 border border-zinc-800 rounded-2xl p-6 shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center shrink-0">
            <AlertTriangle className="h-5 w-5 text-red-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-zinc-50" style={{ fontFamily: "var(--font-heading)" }}>Reset {org.name}?</h2>
            <p className="mt-2 text-sm text-zinc-300">
              This permanently deletes every business record for this organization and returns their workspace to its initial state.
            </p>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button disabled={busy} onClick={onCancel} className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-sm disabled:opacity-50" data-testid="admin-reset-cancel">Cancel</button>
          <button disabled={busy} onClick={run} className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="admin-reset-confirm">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {busy ? "Resetting…" : "Reset Organization"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [orgs, setOrgs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetTarget, setResetTarget] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const [s, o] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/organizations?limit=100"),
      ]);
      setStats(s.data);
      setOrgs(o.data.organizations || []);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="text-sm text-zinc-500" data-testid="admin-loading">Loading admin dashboard…</div>;
  }

  return (
    <div className="max-w-7xl mx-auto space-y-10" data-testid="admin-page">
      <header className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert className="h-4 w-4 text-orange-500" />
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500/80">Administrator</p>
          </div>
          <h1
            className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Platform overview
          </h1>
          <p className="mt-2 text-zinc-400 max-w-2xl">
            Cluster-wide health, recent activity, and per-organization controls.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-white border border-zinc-800 hover:border-zinc-700 px-3 py-1.5 rounded-lg transition-colors"
          data-testid="admin-refresh-btn"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </header>

      {/* Totals */}
      <section>
        <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 mb-4">Totals</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <TotalCard label="Organizations" value={stats.totals.organizations} icon={Building2} testid="admin-total-orgs" />
          <TotalCard label="Users"         value={stats.totals.users}         icon={Users}     testid="admin-total-users" />
          <TotalCard label="Imports"       value={stats.totals.imports}       icon={Upload}    testid="admin-total-imports" />
          <TotalCard label="Orders"        value={stats.totals.orders}        icon={ShoppingBag} testid="admin-total-orders" />
          <TotalCard label="Products"      value={stats.totals.products}      icon={Package}   testid="admin-total-products" />
          <TotalCard label="Customers"     value={stats.totals.customers}     icon={HeartHandshake} testid="admin-total-customers" />
        </div>
      </section>

      {/* System health + Storage */}
      <section className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid="admin-system-health">
          <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 flex items-center gap-2 mb-4">
            <Database className="h-3.5 w-3.5" /> System health
          </h3>
          <dl className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <dt className="text-zinc-400">Database</dt>
              <dd className={`capitalize ${stats.system_health.database === "healthy" ? "text-emerald-400" : "text-amber-400"}`}>{stats.system_health.database}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-zinc-400">Storage</dt>
              <dd className={`capitalize ${stats.system_health.storage === "healthy" ? "text-emerald-400" : "text-amber-400"}`}>{stats.system_health.storage}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-zinc-400">Active users (24h)</dt>
              <dd className="text-zinc-100 tabular-nums">{num(stats.system_health.active_users_24h)}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-zinc-400">Last import</dt>
              <dd className="text-zinc-100 text-xs">{stats.system_health.latest_import_at ? new Date(stats.system_health.latest_import_at).toLocaleString() : "—"}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-zinc-400">Logins in last 7d</dt>
              <dd className="text-zinc-100 tabular-nums">{num(stats.activity_7d.logins)}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-zinc-400">Imports in last 7d</dt>
              <dd className="text-zinc-100 tabular-nums">{num(stats.activity_7d.imports)}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-zinc-400">Sign-ups in last 7d</dt>
              <dd className="text-zinc-100 tabular-nums">{num(stats.activity_7d.signups)}</dd>
            </div>
          </dl>
        </div>

        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid="admin-storage">
          <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 flex items-center gap-2 mb-4">
            <HardDrive className="h-3.5 w-3.5" /> Storage usage
          </h3>
          <p className="text-3xl font-semibold text-zinc-50 tabular-nums" style={{ fontFamily: "var(--font-heading)" }} data-testid="admin-storage-total">
            {bytesToLabel(stats.storage.total_bytes)}
          </p>
          <p className="mt-1 text-xs text-zinc-500">across business collections</p>
          <ul className="mt-4 space-y-1.5 text-sm">
            {Object.entries(stats.storage.by_collection).map(([coll, size]) => (
              <li key={coll} className="flex items-center justify-between text-zinc-400">
                <span className="capitalize">{coll.replace(/_/g, " ")}</span>
                <span className="tabular-nums text-zinc-200">{bytesToLabel(size)}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Recent activity */}
      <section data-testid="admin-recent-activity">
        <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 flex items-center gap-2 mb-4">
          <Activity className="h-3.5 w-3.5" /> Recent activity
        </h2>
        {stats.recent_activity.length === 0 ? (
          <p className="text-sm text-zinc-500">No activity recorded.</p>
        ) : (
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 overflow-hidden">
            <ul className="divide-y divide-zinc-800/60">
              {stats.recent_activity.map((a, i) => (
                <li key={i} className="flex items-center gap-4 px-4 py-3 text-sm" data-testid={`admin-activity-${i}`}>
                  <span className="text-xs text-zinc-500 tabular-nums w-32">{new Date(a.created_at).toLocaleString()}</span>
                  <span className="text-zinc-200 truncate flex-1">{a.user_name || "System"}</span>
                  <span className="text-xs px-2 py-0.5 rounded-md bg-zinc-800 text-zinc-300">{a.action}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Organizations table */}
      <section data-testid="admin-org-table">
        <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 flex items-center gap-2 mb-4">
          <Building2 className="h-3.5 w-3.5" /> Organizations
        </h2>
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 overflow-hidden">
          <table className="w-full">
            <thead className="bg-zinc-900/60 text-[10px] uppercase tracking-[0.14em] text-zinc-500">
              <tr>
                <th className="px-4 py-2 text-left font-bold">Name</th>
                <th className="px-4 py-2 text-right font-bold">Users</th>
                <th className="px-4 py-2 text-right font-bold">Imports</th>
                <th className="px-4 py-2 text-right font-bold">Orders</th>
                <th className="px-4 py-2 text-right font-bold">Products</th>
                <th className="px-4 py-2 text-right font-bold">Customers</th>
                <th className="px-4 py-2 text-right font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(orgs || []).map((o) => (
                <tr key={o.organization_id} className="border-t border-zinc-800/60 hover:bg-zinc-800/20" data-testid={`admin-org-${o.organization_id}`}>
                  <td className="px-4 py-2 text-sm text-zinc-100 truncate max-w-[240px]">{o.name}</td>
                  <td className="px-4 py-2 text-sm text-zinc-300 tabular-nums text-right">{num(o.users)}</td>
                  <td className="px-4 py-2 text-sm text-zinc-300 tabular-nums text-right">{num(o.imports)}</td>
                  <td className="px-4 py-2 text-sm text-zinc-300 tabular-nums text-right">{num(o.orders)}</td>
                  <td className="px-4 py-2 text-sm text-zinc-300 tabular-nums text-right">{num(o.products)}</td>
                  <td className="px-4 py-2 text-sm text-zinc-300 tabular-nums text-right">{num(o.customers)}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => setResetTarget(o)}
                      className="inline-flex items-center gap-1 text-xs text-red-300 hover:text-red-200 border border-red-500/30 hover:border-red-500/60 hover:bg-red-500/10 px-2.5 py-1 rounded-md transition-colors"
                      data-testid={`admin-reset-org-${o.organization_id}`}
                    >
                      <Trash2 className="h-3 w-3" /> Reset
                    </button>
                  </td>
                </tr>
              ))}
              {(orgs || []).length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-zinc-500">No organizations.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {resetTarget && (
        <ResetOrgDialog
          org={resetTarget}
          onCancel={() => setResetTarget(null)}
          onConfirmed={() => { setResetTarget(null); load(); }}
        />
      )}
    </div>
  );
}
